// Helpers for date formatting used by Navigator Reports
// Exports a formatter that converts an ISO/UTC timestamp into the user's
// local timezone with a short timezone name, e.g. "Jul 14, 2026 05:05 AM IST".
export const formatToLocalWithTZ = (isoTs?: string | null): string => {
  if (!isoTs) return '';
  try {
    const d = new Date(isoTs);
    if (isNaN(d.getTime())) return String(isoTs);

    const formatter = new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(d);
    const partMap: Record<string, string> = {};
    parts.forEach((p) => { if (p.type && p.value) partMap[p.type] = p.value; });

    const month = partMap.month ?? '';
    const day = partMap.day ?? '';
    const year = partMap.year ?? '';
    const hour = partMap.hour ?? '';
    const minute = partMap.minute ?? '';
    const dayPeriod = partMap.dayPeriod ?? '';

    // Use the formatter's own resolved IANA timezone (more reliable than a
    // separate Intl.DateTimeFormat() call which may behave differently with
    // polyfills). Also fall back to a new instance for extra safety.
    const resolvedIana = formatter.resolvedOptions().timeZone ||
                         Intl.DateTimeFormat().resolvedOptions().timeZone || '';

    // IANA timezone → short abbreviation.
    // Includes 'Asia/Calcutta' (deprecated alias for 'Asia/Kolkata' still
    // returned by some environments) and DST variants for US timezones.
    const ianaMap: Record<string, string> = {
      'Asia/Kolkata': 'IST',
      'Asia/Calcutta': 'IST',   // deprecated alias — still seen in some runtimes
      'Asia/Colombo': 'IST',
      'Asia/Kathmandu': 'NPT',
      'Asia/Shanghai': 'CST',
      'Asia/Tokyo': 'JST',
      'Australia/Sydney': 'AEST',
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'America/Chicago': 'CST',
      'America/Winnipeg': 'CST',
      'America/Belize': 'CST',
      'America/New_York': 'EST',
      'America/Los_Angeles': 'PST',
      'America/Denver': 'MST',
      'America/Phoenix': 'MST',
      'America/Anchorage': 'AKST',
      'Pacific/Honolulu': 'HST',
    };

    // Long timezone name → short abbreviation (includes DST variants so the
    // 'long' formatter fallback works year-round for US timezones too).
    const longNameMap: Record<string, string> = {
      'India Standard Time': 'IST',
      'British Summer Time': 'BST',
      'Greenwich Mean Time': 'GMT',
      'Central European Time': 'CET',
      'Eastern Standard Time': 'EST',
      'Eastern Daylight Time': 'EDT',
      'Central Standard Time': 'CST',
      'Central Daylight Time': 'CDT',
      'Pacific Standard Time': 'PST',
      'Pacific Daylight Time': 'PDT',
      'Mountain Standard Time': 'MST',
      'Mountain Daylight Time': 'MDT',
      'Alaska Standard Time': 'AKST',
      'Alaska Daylight Time': 'AKDT',
      'Hawaii-Aleutian Standard Time': 'HST',
    };

    // Obtain the long-form timezone name from a separate formatter as a
    // reliable fallback — e.g. Chrome returns 'India Standard Time' for IST.
    let longTzName = '';
    try {
      const longFormatter = new Intl.DateTimeFormat(undefined, { timeZoneName: 'long' });
      const longParts = longFormatter.formatToParts(d);
      longTzName = longParts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch { /* ignore */ }

    let tz = partMap.timeZoneName ?? '';

    if (resolvedIana && ianaMap[resolvedIana]) {
      // Best path: known IANA timezone → direct abbreviation lookup
      tz = ianaMap[resolvedIana];
    } else if (tz && longNameMap[tz]) {
      // Short formatter already gave a long name (some locales/runtimes do this)
      tz = longNameMap[tz];
    } else if (longTzName && longNameMap[longTzName]) {
      // Long formatter gave a recognisable full name → map to abbreviation
      tz = longNameMap[longTzName];
    } else if (tz && /GMT|UTC|\+|\-/i.test(tz)) {
      // Offset string like 'GMT+5:30' — avoid extracting 'GMT' via regex.
      // At this point all IANA / long-name lookups failed; keep the offset
      // as the display value (e.g. '+05:30') rather than the misleading 'GMT'.
      tz = tz.replace(/\s/g, '');
    } else if (tz) {
      // Non-offset string — extract the uppercase abbreviation
      const m = (tz.match(/[A-Z]{2,4}/) || [])[0];
      if (m) tz = m;
      else tz = tz.replace(/[^A-Za-z]/g, '').slice(0, 4).toUpperCase();
    } else {
      tz = '';
    }

    const datePart = `${month} ${day}, ${year}`.trim();
    const timePart = `${hour}:${minute} ${dayPeriod}`.trim();
    return tz ? `${datePart} ${timePart} ${tz}` : `${datePart} ${timePart}`;
  } catch {
    return String(isoTs);
  }
};

/**
 * Formats a recommendation window from two date strings (YYYY-MM-DD) into a
 * human-readable range, e.g. "Jun 7 to Jun 13, 2026".
 * Both dates are interpreted as local (no timezone conversion needed — they are
 * plain calendar dates from the API, not UTC timestamps).
 */
export const formatRecommendationWindow = (start?: string | null, end?: string | null): string => {
  if (!start && !end) return '';
  const parseDate = (dateStr: string): Date | null => {
    // Append T00:00:00 to avoid UTC midnight shifting to previous day
    const d = new Date(`${dateStr}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  };

  const monthDay = (d: Date): string =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  if (start && end) {
    const s = parseDate(start);
    const e = parseDate(end);
    if (s && e) {
      const endYear = e.getFullYear();
      return `${monthDay(s)} to ${monthDay(e)}, ${endYear}`;
    }
  }
  if (start) {
    const s = parseDate(start);
    return s ? monthDay(s) : start;
  }
  if (end) {
    const e = parseDate(end!);
    return e ? monthDay(e) : end!;
  }
  return '';
};

export default formatToLocalWithTZ;
